"use client";
import axios from "axios";
import toast from "react-hot-toast";
import { useFormContext } from "react-hook-form";
import z from "zod";
import { formSchema } from "./TripAuditInspection";
import UnorderListIcon from "../icons/UnorderListIcon";
import { Tooltip } from "antd";
import InfoIcon from "../icons/InfoIcon";
import { AxiosErrorResponse } from "../../../interface/AxiosErrorResponse";
import { pricingPlans } from "../../../static_data/data";
import { logError } from "../../../lib/sentry";
import PlanInfoTooltip from "./PlanInfoTooltip";
import { handleStart } from "../utils/helper";
import apiConfig from "../../../lib/config";
type FormData = z.infer<typeof formSchema>;

const InspectionWorkflow = ({
  setShowInvoiceForm,
  setShowInsurannceCover,
}: {
  setShowInvoiceForm: (show: boolean) => void;
  setShowInsurannceCover: (show: boolean) => void;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useFormContext<FormData>();
  const inspectionPlan = watch("inspection.plan");
  const durations = watch("inspection.access_limit_time");
  // initialize default values if not set
  // useEffect(() => {
  //   if (!inspectionPlan) {
  //     setValue("inspection.plan", "Single", { shouldValidate: true });
  //   }
  //   if (!durations) {
  //     setValue("inspection.duration", "7", { shouldValidate: true });
  //   }
  // }, [setValue, inspectionPlan, durations]);

  // Get dynamic price
  const currentPrice =
    inspectionPlan && durations
      ? (pricingPlans.prices[
          inspectionPlan as keyof typeof pricingPlans.prices
        ]?.[durations] ?? 0)
      : 0;
  const onSubmit = async (data: FormData) => {
    if (inspectionPlan === "Invoice") {
      handleStart();
      setShowInvoiceForm(true);
      return;
    }
    try {
      const orderResponse = await axios.post(
        `${apiConfig.api.baseUrl}/create_order`,
        {
          car_make: data.vehicle.make,
          car_model: data.vehicle.model,
          car_year: Number(data.vehicle.year),
          fuel_type: data.vehicle.fuelType,
          exterior_color: data.vehicle.color || null,
          car_owner_name: data.owner.fullName,
          car_owner_email: data.owner.email,
          car_owner_phone: data.owner.phone,
          car_owner_address: data.owner.address || null,
          driver_name: data.driver.fullName,
          driver_email: data.driver.email,
          driver_phone: data.driver.phone,
          driving_license: data.driver.license || null,
          driver_address: data.driver.address || null,
          inspection_type:
            data.inspection.plan?.toLowerCase() === "single"
              ? "single"
              : "dual",
          access_limit_time: Number(data.inspection.access_limit_time),
        },
        { headers: { "Content-Type": "application/json" } },
      );
      const tripId = orderResponse.data.trip_id;
      const customerEmail = data?.owner?.email ?? "";
      const createSessionData = {
        // success_url: "https://fleetblox.com/trip-audit/payment-success",
        success_url: `https://drivestamp.com/tripwise/payment-success?email=${customerEmail}`,
        cancel_url: "https://drivestamp.com/tripwise/payment-failed",
      };
      // Create payment intent
      const paymentResponse = await axios.post(
        `${apiConfig.api.baseUrl}/create_checkout_session?trip_id=${tripId}`,
        createSessionData,
      );
      if (paymentResponse?.data?.checkout_url) {
        // router.push(paymentResponse.data.checkout_url);
        window.open(paymentResponse.data.checkout_url, "_blank");
      }
    } catch (error) {
      const axiosError = error as AxiosErrorResponse;
      const errorMessage =
        axiosError?.response?.data?.error?.message ||
        "An unexpected error occurred";
      toast.error(
        `${errorMessage || "Failed to create order. Please try again."}`,
      );
      logError(error, {
        feature: `Create checkout session`,
        action: `errorMessage`,
        extra: {
          endpoint: `/create_checkout_session`,
        },
      });
    }
  };

  return (
    <div className="mt-10">
      <div className="text-center mb-5 md:mb-10">
        <h2 className="text-[18px] md:text-[22px] font-bold text-[#303030]">
          Stamp workflow
        </h2>
        <p className="text-[14px] leading-5 text-[#6F6464]">
          Select a stamp plan suitable for your use case
        </p>
      </div>
      <div className="mb-2.5">
        {pricingPlans.plans.map((plan) => {
          const isSelected = inspectionPlan === plan.value;
          // ─── Decide which price to show in this card ───
          let displayPrice = 0;
          if (isSelected) {
            // Selected → use current duration
            displayPrice =
              pricingPlans.prices[
                plan.value as keyof typeof pricingPlans.prices
              ]?.[durations] ?? 0;
          } else {
            // Not selected → always show the 7-day (default) price
            displayPrice =
              pricingPlans.prices[
                plan.value as keyof typeof pricingPlans.prices
              ]?.["30"] ?? 0;
          }
          return (
            <div
              key={plan.id}
              className={`px-4 py-5 border rounded-[10px] mb-5 ${
                inspectionPlan === plan.value
                  ? "border-[#2D65F2] bg-[#F5F9FC]"
                  : "border-[#DFDFDF] bg-white"
              }`}
            >
              <label className="flex items-center gap-[5px] cursor-pointer">
                <input
                  type="radio"
                  value={plan.value}
                  {...register("inspection.plan")}
                />
                <div className="flex items-center gap-1.5">
                  <p
                    className={`${
                      inspectionPlan === plan.value
                        ? "text-[#151515]"
                        : "text-[#6F6464]"
                    } text-[14px] md:text-[16px] font-openSans font-bold capitalize`}
                  >
                    {plan.name} {plan.subtitle && `(${plan.subtitle})`}
                  </p>
                  <Tooltip
                    placement="bottom"
                    title={<PlanInfoTooltip planValue={plan.value} />}
                    color="#ffffff"
                    mouseEnterDelay={0.15}
                    overlayClassName="tooltipOverlay"
                    overlayInnerStyle={{ padding: 0 }}
                  >
                    <span className="inline-flex cursor-help opacity-70 hover:opacity-100 transition-opacity">
                      <InfoIcon fill="#999999" size={20} />
                    </span>
                  </Tooltip>
                </div>
              </label>
              <div className="flex flex-col md:flex-row items-center justify-between mt-[30px]">
                <div className="flex items-start text-[#04082C] my-5">
                  <span className="text-[36px] font-bold">
                    ${Math.floor(displayPrice)}
                  </span>
                  <span className="text-[14px] font-semibold self-end mb-1">
                    .{(displayPrice % 1).toFixed(2).slice(2)}
                  </span>
                </div>

                {/* Render features if available */}
                {plan.features && plan.features.length > 0 && (
                  <div className="space-y-3 mt-4">
                    {plan.features.map((feature, index) => (
                      <Tooltip
                        key={index}
                        placement="top"
                        title={feature?.des || ""}
                      >
                        <div className="flex  items-center">
                          <div className="w-5  text-[#2D65F2]">
                            <UnorderListIcon />
                          </div>
                          <p className="text-[14px] text-[#04082C] leading-5 font-openSans">
                            {feature?.title || ""}
                          </p>
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>
              {/* note section */}
              {plan.note && (
                <p className="text-[#303030] text-[12px] font-semibold leading-4">
                  Note: {plan.note}
                </p>
              )}
            </div>
          );
        })}
      </div>
      {errors.inspection?.plan && (
        <p className="text-red-500 text-sm">{errors.inspection.plan.message}</p>
      )}
      {/* <div className="mb-2 mt-10">
        <h3 className="text-[18px] font-semibold text-[#303030] mb-4">
          Trip duration
        </h3>

        <div className="flex gap-[5px] flex-col md:flex-row items-center">
          {inspectionPlan === "Single" ? (
            <div
              className={`border rounded-md flex p-4 gap-[5px] justify-between w-full md:w-[200px] border-[#2D65F2] `}
            >
              <label className="flex items-center gap-[5px]">
                <input
                  type="radio"
                  value={7}
                  {...register("inspection.duration")}
                />{" "}
                <p
                  className={`text-[16px] font-openSans capitalize font-bold text-[#151515]`}
                >
                  7 Days
                </p>
              </label>
              <p className="text-[#7D7D7D] text-[12px] font-openSans">
                Use within <br /> 7 days
              </p>
            </div>
          ) : (
            ["7", "15", "30"].map((days) => (
              <div
                key={days}
                className={`border rounded-md flex p-4 gap-[5px] justify-between w-full md:w-[180px] ${
                  durations === days ? "border-[#2D65F2]" : "border-[#DFDFDF]"
                }`}
              >
                <label className="flex items-center gap-[5px]">
                  <input
                    type="radio"
                    value={days}
                    {...register("inspection.duration")}
                  />{" "}
                  <p
                    className={`text-[16px] font-openSans capitalize font-bold ${
                      durations === days
                        ? "text-[#151515]  "
                        : " text-[#6F6464]"
                    }`}
                  >
                    {days} Days
                  </p>
                </label>
                <p className="text-[#7D7D7D] text-[12px] font-openSans">
                  Use within <br /> {days} days
                </p>
              </div>
            ))
          )}
        </div>
      </div>
      {errors.inspection?.duration && (
        <p className="text-red-500 text-sm">
          {errors.inspection.duration.message}
        </p>
      )} */}

      {inspectionPlan === "Invoice" ? (
        <button
          onClick={handleSubmit(onSubmit)}
          className="bg-[#2D65F2] text-white p-3 rounded-md mt-10 w-full text-[14px] cursor-pointer font-semibold"
        >
          Proceed to Next{" "}
          {currentPrice > 0 ? ` – $${currentPrice.toFixed(2)}` : ""}
        </button>
      ) : (
        <button
          className="bg-[#2D65F2] text-white p-3 rounded-md mt-10 w-full text-[14px] cursor-pointer font-semibold"
          onClick={() => {
            handleStart();
            setShowInsurannceCover(true);
          }}
        >
          Proceed to next
          {currentPrice > 0 ? ` – $${currentPrice.toFixed(2)}` : ""}
        </button>
      )}
    </div>
  );
};

export default InspectionWorkflow;
